import React, { useLayoutEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { matchPath, useHistory, useLocation } from '../../utils/reactRouterCompatibility';
import { hideMenu, setMenu } from '../../../store/actions/CommonAction';
import { useDispatch, useSelector } from 'react-redux';

const ItemContainer = styled(Box, {
  shouldForwardProp: prop => !['selected', 'isMenuOpen'].includes(prop)
})(({ theme, selected, isMenuOpen }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1.5, 1),
  cursor: 'pointer',
  borderRadius: theme.spacing(1),
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  minWidth: '80px',

  ...(selected && {
    background: 'rgba(255, 145, 0, 0.12)',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: '3px',
      height: '60%',
      background: theme.palette.primary.gradient,
      borderRadius: '0 2px 2px 0',
    },
  }),

  ...(isMenuOpen && !selected && {
    background: 'rgba(255, 255, 255, 0.05)',
  }),

  '&:hover': {
    background: selected ? 'rgba(255, 145, 0, 0.18)' : 'rgba(255, 255, 255, 0.08)',
    '& .menu-icon': {
      transform: 'scale(1.1)',
    },
  },
}));

const IconWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected'
})(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(0.5),
  transition: 'transform 0.2s ease-in-out',
  color: selected ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.7)',
  '& svg': {
    fontSize: '1.75rem',
  },
}));

const MenuText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'selected'
})(({ theme, selected }) => ({
  fontSize: '0.65rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  textAlign: 'center',
  lineHeight: 1.2,
  color: selected ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.7)',
  transition: 'color 0.2s ease-in-out',
}));

const DrawerItem = ({
  Icon,
  text,
  selectedRoutes = [],
  to = undefined,
  menu = undefined,
}) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [selected, setSelected] = useState(false);
  const currentMenu = useSelector(state => state.common.menu);

  const isMenuOpen = menu && currentMenu === menu;

  const handleSetMenu = (menuType) => dispatch(setMenu(menuType));
  const handleHideMenu = () => dispatch(hideMenu());

  useLayoutEffect(() => {
    let matched = false;
    for (let i = 0; i < selectedRoutes.length; i++) {
      const routePattern = typeof selectedRoutes[i] === 'string'
        ? selectedRoutes[i]
        : (selectedRoutes[i]?.path || '');

      if (routePattern && matchPath(routePattern, location.pathname)) {
        matched = true;
        break;
      }
    }
    setSelected(matched);
  }, [location, selectedRoutes]);

  const handleClick = () => {
    if (to) {
      history.push(to);
      handleHideMenu();
    }

    if (menu) {
      if (currentMenu === menu) {
        handleHideMenu();
      } else {
        handleSetMenu(menu);
      }
    }
  };

  return (
    <ItemContainer
      onClick={handleClick}
      selected={selected}
      isMenuOpen={isMenuOpen}
    >
      <IconWrapper selected={selected || isMenuOpen} className="menu-icon">
        <Icon />
      </IconWrapper>
      <MenuText selected={selected || isMenuOpen}>
        {text}
      </MenuText>
    </ItemContainer>
  );
};

export default DrawerItem;